import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Calendar, Users, Download, Video, FileText, ExternalLink, Send, Lock, CheckCircle, Play, Clock, Trophy, Link as LinkIcon, Apple } from 'lucide-react';
import { Button } from '../components/ui/button';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ChallengeDetail = () => {
  const { challengeId } = useParams();
  const navigate = useNavigate();
  const [challenge, setChallenge] = useState(null);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isEnrolled, setIsEnrolled] = useState(false);
  const [showTelegramModal, setShowTelegramModal] = useState(false);

  useEffect(() => {
    fetchData();
  }, [challengeId]);

  const fetchData = async () => {
    const token = localStorage.getItem('session_token');
    
    try {
      // Obtener datos del usuario
      if (token) {
        const userRes = await axios.get(`${BACKEND_URL}/api/auth/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setUser(userRes.data);
        
        // Verificar si está inscrito
        const purchasedChallenges = userRes.data.purchased_challenges || [];
        if (purchasedChallenges.includes(challengeId)) {
          setIsEnrolled(true);
        }
      }

      // Obtener datos del challenge
      const challengeRes = await axios.get(`${BACKEND_URL}/api/challenges`);
      const foundChallenge = challengeRes.data.find(c => c.challenge_id === challengeId);
      
      if (foundChallenge) {
        setChallenge(foundChallenge);
      } else {
        toast.error('Challenge no encontrado');
        navigate('/challenges');
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  // Verificar si el usuario tiene acceso gratuito a este challenge
  const hasFreeChallengeAccess = () => {
    if (!challenge || !user) return false;
    
    const accessType = challenge.access_type;
    const accessLevels = challenge.access_levels || [];
    const legacyLevel = challenge.level;
    const legacyLevels = challenge.levels || [];
    
    if (accessType === 'free') return true;
    
    const levelMap = {
      'principiante': 'nivel_1',
      'intermedio': 'nivel_2',
      'premium': 'premium',
      'avanzado': 'premium'
    };
    const userLevel = levelMap[user.subscription_level] || user.subscription_level;
    
    if (accessType === 'levels' && accessLevels.length > 0) {
      if (accessLevels.includes(userLevel) || accessLevels.includes(user.subscription_level)) {
        return true;
      }
    }
    
    if (legacyLevel === 'premium' || legacyLevels.includes('premium')) {
      if (user.subscription_level === 'premium' || user.subscription_level === 'avanzado') {
        return true;
      }
    }
    
    return false;
  };

  const handleEnroll = async () => {
    const token = localStorage.getItem('session_token');
    
    // Si el usuario tiene acceso gratuito, inscribir directamente (requiere login)
    if (token && hasFreeChallengeAccess()) {
      try {
        await axios.post(`${BACKEND_URL}/api/challenges/${challengeId}/enroll-free`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('¡Te has inscrito al challenge!');
        setIsEnrolled(true);
        await fetchData();
        return;
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Error al inscribirse');
        return;
      }
    }

    // Redirigir a checkout - permite pagar sin estar logueado (guest checkout)
    navigate(`/checkout?challenge=${challengeId}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#09090b] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-500"></div>
      </div>
    );
  }

  if (!challenge) {
    return (
      <div className="min-h-screen bg-[#09090b] flex items-center justify-center">
        <div className="text-center">
          <p className="text-xl text-zinc-400">Challenge no encontrado</p>
          <Link to="/challenges">
            <Button className="mt-4 bg-pink-600 hover:bg-pink-500 rounded-full">
              Volver a Challenges
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  // Si el usuario NO está inscrito, mostrar página de venta
  if (!isEnrolled) {
    return (
      <div className="min-h-screen bg-[#09090b] py-24">
        <div className="max-w-4xl mx-auto px-6">
          {/* Header */}
          <Link to="/challenges" className="inline-flex items-center text-zinc-400 hover:text-white mb-8">
            <ArrowLeft className="w-5 h-5 mr-2" />
            Volver a Challenges
          </Link>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Imagen */}
            <div className="relative">
              {challenge.image_url ? (
                <img 
                  src={challenge.image_url.startsWith('http') ? challenge.image_url : `${BACKEND_URL}${challenge.image_url}`}
                  alt={challenge.title}
                  className="w-full rounded-3xl object-cover aspect-square"
                />
              ) : (
                <div className="w-full rounded-3xl bg-zinc-800 aspect-square flex items-center justify-center">
                  <Trophy className="w-20 h-20 text-zinc-600" />
                </div>
              )}
              
              {challenge.status === 'coming_soon' && (
                <div className="absolute top-4 left-4 bg-yellow-500 text-black px-4 py-2 rounded-full font-bold flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  Próximamente
                </div>
              )}
            </div>

            {/* Info */}
            <div className="space-y-6">
              <h1 className="text-3xl md:text-4xl font-bold text-white" style={{fontFamily: 'Syne, sans-serif'}}>
                {challenge.title}
              </h1>
              
              <p className="text-zinc-400 text-lg whitespace-pre-line">{challenge.description}</p>

              {/* Fechas */}
              {(challenge.start_date || challenge.end_date) && (
                <div className="flex items-center gap-4 text-zinc-400">
                  <Calendar className="w-5 h-5 text-pink-500" />
                  <span>
                    {challenge.start_date && new Date(challenge.start_date).toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' })}
                    {challenge.start_date && challenge.end_date && ' - '}
                    {challenge.end_date && new Date(challenge.end_date).toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' })}
                  </span>
                </div>
              )}

              {/* Precio y botón */}
              <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-zinc-400">Precio</span>
                  <span className="text-3xl font-bold text-white">${challenge.price || 0}</span>
                </div>

                {challenge.status === 'coming_soon' ? (
                  <Button 
                    disabled
                    className="w-full bg-yellow-600/50 text-yellow-200 rounded-full py-6 font-bold cursor-not-allowed"
                  >
                    <Clock className="w-5 h-5 mr-2" />
                    Próximamente
                  </Button>
                ) : (
                  <Button 
                    onClick={handleEnroll}
                    className="w-full bg-pink-600 hover:bg-pink-500 text-white rounded-full py-6 font-bold text-lg"
                  >
                    Inscribirme Ahora
                  </Button>
                )}
              </div>

              {/* Lo que incluye */}
              {challenge.materials && challenge.materials.length > 0 && (
                <div className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-6">
                  <h3 className="text-lg font-bold text-white mb-4">Lo que incluye:</h3>
                  <ul className="space-y-3">
                    {challenge.materials.map((mat, idx) => (
                      <li key={idx} className="flex items-center gap-3 text-zinc-400">
                        {mat.type === 'video' && <Video className="w-5 h-5 text-blue-400" />}
                        {mat.type === 'pdf' && <FileText className="w-5 h-5 text-red-400" />}
                        {mat.type === 'file' && <Download className="w-5 h-5 text-green-400" />}
                        <span>{mat.name}</span>
                      </li>
                    ))}
                    {(challenge.telegram_link || challenge.whatsapp_link) && (
                      <li className="flex items-center gap-3 text-zinc-400">
                        {challenge.community_type === 'whatsapp' ? (
                          <>
                            <LinkIcon className="w-5 h-5 text-green-400" />
                            <span>Acceso a grupo exclusivo de WhatsApp</span>
                          </>
                        ) : (
                          <>
                            <Send className="w-5 h-5 text-blue-400" />
                            <span>Acceso a grupo exclusivo de Telegram</span>
                          </>
                        )}
                      </li>
                    )}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Si el usuario ESTÁ INSCRITO, mostrar contenido del challenge
  return (
    <div className="min-h-screen bg-[#09090b] py-24">
      <div className="max-w-6xl mx-auto px-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <Link to="/challenges" className="inline-flex items-center text-zinc-400 hover:text-white">
            <ArrowLeft className="w-5 h-5 mr-2" />
            Volver a Challenges
          </Link>
          
          <div className="flex items-center gap-2 bg-green-500/20 text-green-400 px-4 py-2 rounded-full border border-green-500/30">
            <CheckCircle className="w-5 h-5" />
            <span className="font-medium">Inscrita</span>
          </div>
        </div>

        {/* Challenge Info */}
        <div className="bg-zinc-900 border border-zinc-800 rounded-3xl overflow-hidden mb-8">
          <div className="flex flex-col md:flex-row">
            {/* Imagen pequeña */}
            <div className="md:w-1/3">
              {challenge.image_url ? (
                <img 
                  src={challenge.image_url.startsWith('http') ? challenge.image_url : `${BACKEND_URL}${challenge.image_url}`}
                  alt={challenge.title}
                  className="w-full h-48 md:h-full object-cover"
                />
              ) : (
                <div className="w-full h-48 md:h-full bg-zinc-800 flex items-center justify-center">
                  <Trophy className="w-16 h-16 text-zinc-600" />
                </div>
              )}
            </div>

            {/* Info */}
            <div className="p-6 md:w-2/3">
              <h1 className="text-2xl md:text-3xl font-bold text-white mb-4" style={{fontFamily: 'Syne, sans-serif'}}>
                {challenge.title}
              </h1>
              <p className="text-zinc-400 mb-4 whitespace-pre-line">{challenge.description}</p>
              
              <div className="flex flex-wrap gap-4">
                {(challenge.start_date || challenge.end_date) && (
                  <div className="flex items-center gap-2 text-zinc-400 text-sm">
                    <Calendar className="w-4 h-4 text-pink-500" />
                    <span>
                      {challenge.start_date && new Date(challenge.start_date).toLocaleDateString('es-ES')}
                      {challenge.start_date && challenge.end_date && ' - '}
                      {challenge.end_date && new Date(challenge.end_date).toLocaleDateString('es-ES')}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Community Banner - Telegram or WhatsApp */}
        {(challenge.telegram_link || challenge.whatsapp_link) && (
          <div className={`bg-gradient-to-r ${challenge.community_type === 'whatsapp' ? 'from-green-600 to-green-700' : 'from-blue-600 to-blue-700'} rounded-2xl p-6 mb-8`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                  {challenge.community_type === 'whatsapp' ? (
                    <LinkIcon className="w-6 h-6 text-white" />
                  ) : (
                    <Send className="w-6 h-6 text-white" />
                  )}
                </div>
                <div>
                  <h3 className="text-lg font-bold text-white">¡Únete a nuestra comunidad!</h3>
                  <p className={`${challenge.community_type === 'whatsapp' ? 'text-green-200' : 'text-blue-200'} text-sm`}>
                    Conecta con otras participantes del challenge
                  </p>
                </div>
              </div>
              <a
                href={challenge.community_type === 'whatsapp' ? challenge.whatsapp_link : challenge.telegram_link}
                target="_blank"
                rel="noopener noreferrer"
                className={`bg-white ${challenge.community_type === 'whatsapp' ? 'text-green-600 hover:bg-green-50' : 'text-blue-600 hover:bg-blue-50'} px-6 py-3 rounded-full font-bold transition-colors flex items-center gap-2`}
              >
                {challenge.community_type === 'whatsapp' ? (
                  <LinkIcon className="w-5 h-5" />
                ) : (
                  <Send className="w-5 h-5" />
                )}
                Unirme al Grupo
              </a>
            </div>
          </div>
        )}

        {/* Plan Nutricional del Challenge */}
        {challenge.has_nutrition_plan && isEnrolled && (() => {
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          const startDate = challenge.start_date ? new Date(challenge.start_date) : null;
          const endDate = challenge.end_date ? new Date(challenge.end_date) : null;
          if (startDate) startDate.setHours(0, 0, 0, 0);
          if (endDate) endDate.setHours(23, 59, 59, 999);
          
          const hasStarted = !startDate || today >= startDate;
          const hasEnded = endDate && today > endDate;
          
          // Si NO ha iniciado - mostrar bloqueado
          if (!hasStarted) {
            const diffTime = startDate - today;
            const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
            return (
              <div className="bg-gradient-to-r from-green-600/10 to-emerald-600/10 border border-green-500/20 rounded-2xl p-6 mb-8 opacity-70">
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 bg-green-500/10 rounded-full flex items-center justify-center relative">
                      <Apple className="w-7 h-7 text-green-400/50" />
                      <div className="absolute -bottom-1 -right-1 bg-zinc-900 rounded-full p-1">
                        <Lock className="w-4 h-4 text-green-400" />
                      </div>
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-white/70">Plan Nutricional Personalizado</h3>
                      <p className="text-green-300/50 text-sm">
                        Disponible cuando inicie el challenge ({diffDays} {diffDays === 1 ? 'día' : 'días'})
                      </p>
                    </div>
                  </div>
                  <div className="bg-zinc-800/50 text-zinc-400 px-6 py-3 rounded-full font-bold flex items-center gap-2 cursor-not-allowed">
                    <Lock className="w-5 h-5" />
                    Bloqueado
                  </div>
                </div>
              </div>
            );
          }
          
          // Si YA TERMINÓ - mostrar bloqueado
          if (hasEnded) {
            return (
              <div className="bg-gradient-to-r from-green-600/10 to-emerald-600/10 border border-green-500/20 rounded-2xl p-6 mb-8 opacity-70">
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 bg-green-500/10 rounded-full flex items-center justify-center relative">
                      <Apple className="w-7 h-7 text-green-400/50" />
                      <div className="absolute -bottom-1 -right-1 bg-zinc-900 rounded-full p-1">
                        <Lock className="w-4 h-4 text-zinc-500" />
                      </div>
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-white/70">Plan Nutricional Personalizado</h3>
                      <p className="text-zinc-500 text-sm">
                        Este challenge ha finalizado
                      </p>
                    </div>
                  </div>
                  <div className="bg-zinc-800/50 text-zinc-500 px-6 py-3 rounded-full font-bold flex items-center gap-2 cursor-not-allowed">
                    <Lock className="w-5 h-5" />
                    Finalizado
                  </div>
                </div>
              </div>
            );
          }
          
          // Challenge activo - mostrar normal
          return (
            <div className="bg-gradient-to-r from-green-600/20 to-emerald-600/20 border border-green-500/30 rounded-2xl p-6 mb-8">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 bg-green-500/20 rounded-full flex items-center justify-center">
                    <Apple className="w-7 h-7 text-green-400" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-white">Plan Nutricional Personalizado</h3>
                    <p className="text-green-300 text-sm">
                      Calcula tus calorías y crea tu menú según el objetivo del challenge
                    </p>
                  </div>
                </div>
                <Link
                  to={`/challenge/${challengeId}/nutrition`}
                  className="bg-green-500 hover:bg-green-400 text-white px-6 py-3 rounded-full font-bold transition-colors flex items-center gap-2"
                >
                  <Apple className="w-5 h-5" />
                  Crear mi Plan
                </Link>
              </div>
            </div>
          );
        })()}

        {/* Materiales del Challenge */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-3">
            <Download className="w-6 h-6 text-pink-500" />
            Materiales del Challenge
          </h2>

          {(() => {
            // Verificar si el challenge ha iniciado y terminado
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            const startDate = challenge.start_date ? new Date(challenge.start_date) : null;
            const endDate = challenge.end_date ? new Date(challenge.end_date) : null;
            if (startDate) startDate.setHours(0, 0, 0, 0);
            if (endDate) endDate.setHours(23, 59, 59, 999);
            
            const hasStarted = !startDate || today >= startDate;
            const hasEnded = endDate && today > endDate;
            const hasMaterials = challenge.materials && challenge.materials.length > 0;
            
            // Si el challenge YA TERMINÓ - mostrar mensaje de agradecimiento
            if (hasEnded) {
              return (
                <div className="space-y-6">
                  {/* Mensaje de agradecimiento */}
                  <div className="bg-gradient-to-r from-purple-500/10 via-pink-500/10 to-orange-500/10 border border-pink-500/30 rounded-2xl p-8 text-center">
                    <Trophy className="w-16 h-16 text-yellow-500 mx-auto mb-4" />
                    <h3 className="text-2xl font-bold text-white mb-3">
                      ¡Gracias por participar!
                    </h3>
                    <p className="text-zinc-300 text-lg mb-6 max-w-xl mx-auto">
                      Este challenge ha finalizado. Te esperamos en nuestro próximo challenge.
                    </p>
                    <p className="text-zinc-400 mb-8">
                      Puedes seguir visitando nuestra página web y ver nuestros programas, ebooks, academia y próximos challenges para ti.
                    </p>
                    <div className="flex flex-wrap justify-center gap-4">
                      <Link
                        to="/programs"
                        className="bg-pink-600 hover:bg-pink-500 text-white px-6 py-3 rounded-full font-bold transition-colors"
                      >
                        Ver Programas
                      </Link>
                      <Link
                        to="/challenges"
                        className="bg-zinc-800 hover:bg-zinc-700 text-white px-6 py-3 rounded-full font-bold transition-colors"
                      >
                        Próximos Challenges
                      </Link>
                      <Link
                        to="/academia"
                        className="bg-zinc-800 hover:bg-zinc-700 text-white px-6 py-3 rounded-full font-bold transition-colors"
                      >
                        Academia
                      </Link>
                    </div>
                  </div>
                  
                  {/* Mostrar materiales bloqueados */}
                  {hasMaterials && (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {challenge.materials.map((material, idx) => (
                        <div
                          key={idx}
                          className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-6 opacity-50 cursor-not-allowed relative overflow-hidden"
                        >
                          <div className="absolute inset-0 bg-black/40 backdrop-blur-[2px] flex items-center justify-center z-10">
                            <div className="bg-zinc-900/90 rounded-full p-3">
                              <Lock className="w-6 h-6 text-zinc-500" />
                            </div>
                          </div>
                          <div className="flex items-start gap-4">
                            <div className={`w-12 h-12 rounded-xl flex items-center justify-center bg-zinc-800 text-zinc-500`}>
                              {material.type === 'video' && <Video className="w-6 h-6" />}
                              {material.type === 'pdf' && <FileText className="w-6 h-6" />}
                              {material.type === 'file' && <Download className="w-6 h-6" />}
                            </div>
                            <div className="flex-1 min-w-0">
                              <h3 className="font-bold text-white/50 truncate">{material.name}</h3>
                              <p className="text-sm text-zinc-600 uppercase">{material.type}</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            }
            
            // Si NO ha iniciado el challenge, mostrar materiales bloqueados
            if (!hasStarted) {
              const diffTime = startDate - today;
              const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
              
              return (
                <div className="space-y-4">
                  {/* Mensaje de cuenta regresiva */}
                  <div className="bg-gradient-to-r from-pink-500/10 to-purple-500/10 border border-pink-500/30 rounded-2xl p-6 text-center">
                    <Clock className="w-12 h-12 text-pink-500 mx-auto mb-3" />
                    <h3 className="text-xl font-bold text-white mb-2">
                      {diffDays === 0 ? '🔥 ¡El challenge empieza hoy!' : `⏰ Faltan ${diffDays} ${diffDays === 1 ? 'día' : 'días'} para comenzar`}
                    </h3>
                    <p className="text-zinc-400">
                      El contenido se desbloqueará el{' '}
                      <span className="text-pink-400 font-bold">
                        {startDate.toLocaleDateString('es-ES', { 
                          weekday: 'long', 
                          day: 'numeric', 
                          month: 'long' 
                        })}
                      </span>
                    </p>
                  </div>
                  
                  {/* Mostrar materiales bloqueados */}
                  {hasMaterials && (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {challenge.materials.map((material, idx) => (
                        <div
                          key={idx}
                          className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-6 opacity-60 cursor-not-allowed relative overflow-hidden"
                        >
                          <div className="absolute inset-0 bg-black/40 backdrop-blur-[2px] flex items-center justify-center z-10">
                            <div className="bg-zinc-900/90 rounded-full p-3">
                              <Lock className="w-6 h-6 text-pink-500" />
                            </div>
                          </div>
                          <div className="flex items-start gap-4">
                            <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                              material.type === 'video' ? 'bg-blue-500/20 text-blue-400' :
                              material.type === 'pdf' ? 'bg-red-500/20 text-red-400' :
                              'bg-green-500/20 text-green-400'
                            }`}>
                              {material.type === 'video' && <Video className="w-6 h-6" />}
                              {material.type === 'pdf' && <FileText className="w-6 h-6" />}
                              {material.type === 'file' && <Download className="w-6 h-6" />}
                            </div>
                            <div className="flex-1 min-w-0">
                              <h3 className="font-bold text-white truncate">{material.name}</h3>
                              <p className="text-sm text-zinc-500 uppercase">{material.type}</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            }
            
            // Si YA inició y tiene materiales, mostrarlos desbloqueados
            if (hasMaterials) {
              return (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {challenge.materials.map((material, idx) => (
                    <a
                      key={idx}
                      href={material.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="bg-zinc-900 border border-zinc-800 rounded-2xl p-6 hover:border-pink-500/50 transition-all group"
                    >
                      <div className="flex items-start gap-4">
                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                          material.type === 'video' ? 'bg-blue-500/20 text-blue-400' :
                          material.type === 'pdf' ? 'bg-red-500/20 text-red-400' :
                          'bg-green-500/20 text-green-400'
                        }`}>
                          {material.type === 'video' && <Video className="w-6 h-6" />}
                          {material.type === 'pdf' && <FileText className="w-6 h-6" />}
                          {material.type === 'file' && <Download className="w-6 h-6" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-bold text-white group-hover:text-pink-400 transition-colors truncate">
                            {material.name}
                          </h3>
                          <p className="text-sm text-zinc-500 uppercase">{material.type}</p>
                        </div>
                        <ExternalLink className="w-5 h-5 text-zinc-500 group-hover:text-pink-400 transition-colors flex-shrink-0" />
                      </div>
                    </a>
                  ))}
                </div>
              );
            }
            
            // Si ya inició pero NO tiene materiales
            return (
              <div className="bg-zinc-900/50 border border-dashed border-zinc-800 rounded-2xl p-8 text-center">
                <FileText className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
                <p className="text-zinc-400">
                  Aún no hay materiales disponibles para este challenge.
                </p>
              </div>
            );
          })()}
        </div>

        {/* Más acciones */}
        <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-6 text-center">
          <p className="text-zinc-400 mb-4">¿Tienes dudas sobre el challenge?</p>
          <a
            href="https://wa.me/573001234567"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-green-400 hover:text-green-300"
          >
            Contáctanos por WhatsApp
          </a>
        </div>
      </div>
    </div>
  );
};

export default ChallengeDetail;
